import axios from "axios";

// Adresse de l'API : VUE_APP_API_BASE_URL, sinon le poste local
let baseURL = process.env.VUE_APP_API_BASE_URL;
if (!baseURL) {
  const h = window.location.hostname;
  baseURL = (h === "localhost" || h.startsWith("192.168.") || h.startsWith("10.")) ? "http://127.0.0.1:8000" : window.location.origin.replace("://", "://api.");
}

export const API_BASE = baseURL;

const apiClient = axios.create({
  baseURL: `${baseURL}/api/v1`,
  timeout: 60000,
  headers: { "Content-Type": "application/json", Accept: "application/json" }
});

apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** Message non bloquant en bas à droite. */
export function notifier(message, type = "danger") {
  let zone = document.getElementById("toast-zone");
  if (!zone) {
    zone = document.createElement("div");
    zone.id = "toast-zone";
    document.body.appendChild(zone);
  }
  const t = document.createElement("div");
  t.className = `toast-msg toast-${type}`;
  t.setAttribute("role", type === "danger" ? "alert" : "status");
  t.textContent = message;
  zone.appendChild(t);
  setTimeout(() => t.classList.add("sortie"), 5200);
  setTimeout(() => t.remove(), 5600);
}

/** Message lisible renvoyé par l'API. */
export function messageErreur(error, defaut = "L'opération n'a pas abouti.") {
  if (error?.horsLigne) return "Pas de connexion au serveur : rien n'a été enregistré.";
  const d = error?.response?.data;
  return d?.message || d?.detail || d?.["hydra:description"] || d?.error || defaut;
}

function peutRenouveler() {
  try {
    const p = JSON.parse(atob(localStorage.getItem("token").split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return (p.roles || []).includes("ROLE_SUPER_ADMIN") || (p.permissions || []).map(x => String(x).toUpperCase()).includes("ABONNEMENT_GERER");
  } catch (e) {
    return false;
  }
}

apiClient.interceptors.response.use(
  response => response,
  async error => {
    if (!error.response && !axios.isCancel(error)) {
      error.horsLigne = true;
      // « silencieux » : l'appelant gère lui-même la coupure (caisse hors ligne)
      if ((error.config?.method || "get") !== "get" && !error.config?.silencieux) notifier("Pas de connexion au serveur : rien n'a été enregistré.", "warning");
      return Promise.reject(error);
    }
    const s = error.response?.status;
    if (s === 403 && !error.config?.silencieux) notifier(messageErreur(error, "Vous n'avez pas la permission d'effectuer cette action."));
    if (s === 402) {
      const message = messageErreur(error, "L'abonnement de votre entreprise a expiré.");
      if (peutRenouveler()) {
        if (window.location.pathname !== "/mon-abonnement") window.location.href = "/mon-abonnement";
      } else {
        localStorage.removeItem("token");
        if (window.location.pathname !== "/login") window.location.href = "/login?message=" + encodeURIComponent(message);
      }
    }
    if (s === 401 && !String(error.config?.url || "").includes("login")) {
      localStorage.removeItem("token");
      if (window.location.pathname !== "/login") window.location.href = "/login?message=" + encodeURIComponent("Votre session a expiré. Reconnectez-vous.");
    }
    return Promise.reject(error);
  }
);

const api = {
  client: apiClient,
  get: (url, params = {}) => apiClient.get(`/${url}`, { params }),
  post: (url, data = {}) => apiClient.post(`/${url}`, data),
  put: (url, data = {}) => apiClient.put(`/${url}`, data),
  patch: (url, data = {}) => apiClient.patch(`/${url}`, data, { headers: { "Content-Type": "application/merge-patch+json" } }),
  del: (url) => apiClient.delete(`/${url}`),
  envoyerFichier: (url, formData) => apiClient.post(`/${url}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),

  /**
   * Liste paginée et filtrée par le serveur : tous les paramètres sont transmis
   * (page, itemsperpage, search, filtres propres à l'écran), sans les valeurs vides.
   */
  liste(resource, params = {}) {
    const p = {};
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p[k] = v; });
    return apiClient.get(`/${resource}`, { params: p });
  },

  // Compatibilité avec les écrans repris (utilisateurs, rôles, plateforme…)
  getItems(resource, { orderBy, itemsperpage, sortOrder, page, search } = {}) {
    return apiClient.get(`/${resource}`, { params: { orderBy, sortOrder, itemsperpage, page, search } });
  },
  createItem(resource, data, isMultipart = false) {
    return apiClient.post(`/${resource}`, data, { headers: { "Content-Type": isMultipart ? "multipart/form-data" : "application/json" } });
  },
  updateItem(resource, id, data, isMultipart = false) {
    const url = id ? `/${resource}/${id}` : `/${resource}`;
    return isMultipart ? apiClient.post(url, data, { headers: { "Content-Type": "multipart/form-data" } }) : apiClient.put(url, data);
  },
  updatePassword: (data) => apiClient.put(`/users/update_password`, data),
  getMe: () => apiClient.get(`/me`),
  deleteItem: (resource, id) => apiClient.delete(`/${resource}/${id}`),
  custom: (url, method = "PATCH", data = null) => apiClient({ url: `/${url}`, method, data }),
  patchItem: (resource, id, data) => apiClient.patch(`/${resource}/${id}`, data),
  patchMergeItem: (resource, id, data) => apiClient.patch(`/${resource}/${id}`, data, { headers: { "Content-Type": "application/merge-patch+json" } }),
  getItem: (resource, id) => apiClient.get(`/${resource}/${id}`),

  /** Télécharge un fichier protégé (PDF, CSV) avec le jeton. */
  async telecharger(resource, nomFichier, ouvrir = false) {
    const res = await apiClient.get(`/${resource}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (ouvrir) {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = nomFichier;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
};

export default api;
